import React, { useEffect, useState } from 'react';

import type {
  BulkEmailDTO,
  SearchRequestDTO_bulkEmailSearchRequestDTO,
} from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

import AddNewButton from '../../../components/common/AddButton';
import Button from '../../../components/common/Button';
import Card from '../../../components/common/Card';
import FormField from '../../../components/common/FormField';
import AdminLayout from '../../../components/layout/AdminLayout';
import BulkEmailTable from '../../../components/tables/BulkEmailTable';
import { useBulkEmailApi } from '../../../hooks/useBulkEmailApi';
import { confirmDelete } from '../../../utils/sweetAlertUtils';

const BulkEmailList: React.FC = () => {
  const [bulkEmails, setBulkEmails] = useState<BulkEmailDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [subjectSearch, setSubjectSearch] = useState('');

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Bulk Emails', href: '#' },
  ];

  const { search, processBulkEmail, remove } = useBulkEmailApi();
  const fetchBulkEmails = async (
    page: number,
    size: number,
    filters = {},
  ): Promise<void> => {
    const searchRequest: SearchRequestDTO_bulkEmailSearchRequestDTO = {
      filters: {
        subject: subjectSearch || '',
        ...filters,
      },
      page,
      pageSize: size,
      sortBy: 'SentDate',
      sortDescending: true,
    };

    try {
      setIsLoading(true);
      const response = await search(searchRequest);
      if (response && response.success && response.data) {
        const totalRecords = response.data?.totalCount ?? 0;

        setBulkEmails((response.data?.results as BulkEmailDTO[]) || []);
        setTotalRecords(totalRecords);
        setTotalPages(Math.ceil(totalRecords / size));
      }
    } catch (error) {
      console.error('Error fetching bulk emails:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBulkEmails(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent): void => {
    e.preventDefault();
    setCurrentPage(1);
    fetchBulkEmails(1, pageSize);
  };

  const handleClearSearch = (): void => {
    setSubjectSearch('');
    fetchBulkEmails(1, pageSize, {
      subject: '',
    });
  };

  const handleProcessSend = async (dto: BulkEmailDTO): Promise<void> => {
    try {
      await processBulkEmail(dto.id || '');
      await fetchBulkEmails(currentPage, pageSize, { subjectSearch });
    } catch (error) {
      console.error('Error processing bulk email:', error);
    }
  };

  const handleDelete = async (dto: BulkEmailDTO): Promise<void> => {
    const confirmed = await confirmDelete();
    if (!confirmed) {
      return;
    }

    try {
      await remove(dto.id || '');
      await fetchBulkEmails(currentPage, pageSize, { subjectSearch });
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/bulkemails"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
    >
      <Card
        title="Manage Bulk Emails"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton onClick={() => navigate('/admin/bulkemails/create')} />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="subjectSearch"
              label="Subject"
              value={subjectSearch}
              onChange={(e) => setSubjectSearch(e.target.value)}
              placeholder="Search by subject"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <BulkEmailTable
          emails={bulkEmails}
          handleEdit={(dto) => navigate(`/admin/bulkemails/${dto.id}`)}
          handleDelete={handleDelete}
          handleProcessSend={handleProcessSend}
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default BulkEmailList;
